import { Button } from "$components/button/button";
import { ConditionalDisplay } from "$components/conditional-display";
import { makeStyles } from "$components/mui-styles";
import { ValidatingSubmitButton as RHFSubmitButton } from "$components/rhf-fields";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SaveIcon from "@material-ui/icons/Save";
import { useEffect, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { FormattedMessage } from "react-intl";
import useUpdateLineItems from "../hooks/useUpdateLineItems";
import type { InvoiceCardDetailsInfo, LineItem, PriceList } from "../types";
import { EditableInvoiceLineItem } from "./EditableInvoiceLineItem";
import { LINEITEMS } from "./formConstants";
import { useValidationSchema as lineItemSchema } from "./formHelpers";
import type { InvoiceDetailsDispatcher } from "./hooks/useInvoiceDetailsDispatcher";
import { InvoiceLineItem } from "./InvoiceLineItem";

const useStyles = makeStyles((theme) => ({
  header: {
    fontWeight: 700,
    fontSize: 12,
    color: theme.palette.grey[600],
    fontFamily: theme.typography.fontFamily,
    textTransform: "uppercase",
  },
}));

interface Props {
  invoiceId: string;
  invoice: InvoiceCardDetailsInfo;
  priceList: PriceList;
  editable: boolean;
  hasLineItems: boolean;
  setHasLineItems: (state: boolean) => void;
  total: number;
  setTotal: (total: number) => void;
  dispatcher: InvoiceDetailsDispatcher;
  busy?: boolean;
}

export const LineItemSection = ({
  invoiceId,
  invoice,
  priceList,
  editable,
  hasLineItems,
  setHasLineItems,
  total,
  setTotal,
  dispatcher,
  busy,
}: Props) => {
  const classes = useStyles();
  const [newItemCount, setNewItemCount] = useState<number>(0);
  const { updateLineItems, saving } = useUpdateLineItems(invoiceId);

  const lineItemFormMethods = useForm({
    mode: "onTouched",

    resolver: yupResolver(lineItemSchema()),
    defaultValues: { lineItems: [] },
  });
  const {
    control,
    reset: resetFormState,
    formState,
    trigger: triggerValidation,
    setValue,
  } = lineItemFormMethods;

  const watchedInvoiceLineItems: LineItem[] = useWatch({
    name: LINEITEMS,
    defaultValue: [],
    control,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: LINEITEMS,
  });
  const { isDirty } = formState;

  useEffect(() => {
    if (invoice?.lineItems && invoice.lineItems.length > 0) {
      // Check if form has locally added items that could be lost
      const currentFormValues = lineItemFormMethods.getValues();
      const currentLineItems = currentFormValues.lineItems || [];
      const hasLocallyAddedItems = currentLineItems.some(
        (item) => item.tempId !== undefined && !item.id,
      );

      // Never overwrite form state if there are locally added items being worked on
      // or if the form has unsaved changes
      if (!hasLocallyAddedItems && !saving && !isDirty) {
        setValue(LINEITEMS, invoice.lineItems);
        resetFormState(invoice);
      }
    }
  }, [invoice, resetFormState, setValue, lineItemFormMethods, saving, isDirty]);

  useEffect(() => {
    const currHasLineItems = watchedInvoiceLineItems.length > 0;

    if (currHasLineItems !== hasLineItems) {
      setHasLineItems(currHasLineItems);
    }
  }, [watchedInvoiceLineItems, setHasLineItems, hasLineItems]);

  const totalCost: number = watchedInvoiceLineItems
    .filter((l) => !l.disabled)
    .reduce(
      (a, b: LineItem) => a + (b.cost && !Number.isNaN(b.cost) ? b.cost : 0),
      0,
    );

  useEffect(() => {
    if (totalCost !== total) {
      setTotal(totalCost);
    }
  }, [totalCost, setTotal, total]);

  const saveItems = async (formValues) => {
    dispatcher.sendBusy();
    try {
      await updateLineItems(formValues?.lineItems, priceList);
      resetFormState(formValues);
    } finally {
      dispatcher.sendBusy(false);
    }
  };

  const removeItem = (index) => {
    remove(index);
  };

  const addNewItem = async () => {
    append({
      id: "",
      tempId: newItemCount,
      formattedPrice: "",
      quantity: 1,
      supplier: "GOVERNMENT",
      priceListItem: {
        name: "",
        uuid: "",
        priceListItemType: "",
        externalReference: {
          origin: "",
          foreignId: "",
        },
        activeTariffId: null,
        revenueDepartment: null,
      },
      automated: false,
      disabled: false,
    });
    await triggerValidation();
    setNewItemCount(newItemCount + 1);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormProvider {...lineItemFormMethods}>
          <form
            name="lineItemsForms"
            onSubmit={lineItemFormMethods.handleSubmit(saveItems)}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.header}>
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.header.date"
                        defaultMessage="Date"
                      />
                    </TableCell>
                    <TableCell className={classes.header}>
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.header.item"
                        defaultMessage="Item"
                      />
                    </TableCell>
                    <TableCell className={classes.header}>
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.header.revenueDepartment"
                        defaultMessage="Revenue Dpt"
                      />
                    </TableCell>
                    <TableCell className={classes.header}>
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.header.supplier"
                        defaultMessage="Supplier"
                      />
                    </TableCell>
                    <TableCell className={classes.header}>
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.header.quantity"
                        defaultMessage="Qty"
                      />
                    </TableCell>
                    <TableCell className={classes.header}>
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.header.price"
                        defaultMessage="Price"
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields?.map((lineItem, index) => {
                    if (editable)
                      return (
                        <EditableInvoiceLineItem
                          key={`${invoiceId}-${lineItem.tempId || lineItem.id}`}
                          lineItem={lineItem}
                          index={index}
                          removeItem={removeItem}
                          priceList={priceList}
                        />
                      );
                    return (
                      <InvoiceLineItem
                        key={`${invoiceId}-${lineItem.tempId || lineItem.id}`}
                        {...lineItem}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <ConditionalDisplay condition={!hasLineItems}>
              <Box p={2} textAlign="center">
                <Typography variant="subtitle1">
                  <FormattedMessage
                    id="invoicing.invoice.lineItem.empty.title"
                    defaultMessage="No items"
                  />
                </Typography>
                <ConditionalDisplay condition={!editable}>
                  <Typography variant="subtitle2">
                    <FormattedMessage
                      id="invoicing.invoice.lineItem.empty.subtitle"
                      defaultMessage="Enable editing to add items"
                    />
                  </Typography>
                </ConditionalDisplay>
              </Box>
            </ConditionalDisplay>
            <ConditionalDisplay condition={editable}>
              <Grid container>
                <Grid item xs={6}>
                  <Box py={2}>
                    <Button
                      color="secondary"
                      onClick={addNewItem}
                      startIcon={<AddIcon />}
                      data-testid="invoicing.addItem.button"
                    >
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.addItem"
                        defaultMessage="Add Item"
                      />
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box py={2} textAlign="right">
                    <RHFSubmitButton
                      color="secondary"
                      type="submit"
                      disabled={!isDirty || busy}
                      loading={saving}
                      startIcon={<SaveIcon />}
                      data-testid="invoicing.saveItems.button"
                    >
                      <FormattedMessage
                        id="invoicing.invoice.lineItem.saveItems"
                        defaultMessage="Save line items"
                      />
                    </RHFSubmitButton>
                  </Box>
                </Grid>
              </Grid>
            </ConditionalDisplay>
          </form>
        </FormProvider>
      </Grid>
    </Grid>
  );
};
